import { action } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { tracked } from "@glimmer/tracking"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import type CourseCalendarEditor from "cc-frontend/services/course-calendar-editor"
import type PlanbookEditor from "cc-frontend/services/planbook-editor"

interface Sig {
  Args: {
    courses: Array<Course>
    dateString: string
    goBack: Function
    planbook: Planbook
    closeMenu: Function
    rotationCalendar: RotationCalendar
  }
}

export default class DayMenuAddNoSchoolDayComponent extends Component<Sig> {
  @service courseCalendarEditor!: CourseCalendarEditor
  @service planbookEditor!: PlanbookEditor

  /**
   * State machine.
   * 1. BUMP_OR_DELETE if the original date has any lessons.
   * 2. EDIT_ANNOTATION.
   * 3. BUMP_OR_DELETE_2 if you skipped BUMP_OR_DELETE but expanded your date range to one that includes lessons.
   * 4. finish() creates the annotation and closes.
   */
  @tracked
  menuName: "BUMP_OR_DELETE" | "EDIT_ANNOTATION" | "BUMP_OR_DELETE_2"
  @tracked
  moreThanOneLesson: boolean
  bumpDeleteOrNothing: "BUMP" | "DELETE" | "NOTHING" = "NOTHING"
  @tracked
  annotationArgs: {
    title: string
    description: string
    startDate: string
    endDate: string
  } | null = null

  constructor(owner: unknown, args: Sig["Args"]) {
    super(owner, args)

    const coursesWithLessons = this.args.courses.filter((course) =>
      this.courseCalendarEditor.doesCourseDateHaveLesson(course, this.args.dateString)
    )
    this.menuName = coursesWithLessons.length > 0 ? "BUMP_OR_DELETE" : "EDIT_ANNOTATION"
    this.moreThanOneLesson = coursesWithLessons.length > 1
  }

  @action
  afterBumpOrDelete(bumpDeleteOrNothing: "BUMP" | "DELETE") {
    this.bumpDeleteOrNothing = bumpDeleteOrNothing
    this.menuName = "EDIT_ANNOTATION"
  }

  @action
  goBackFromAnnotation() {
    if (this.bumpDeleteOrNothing !== "NOTHING") {
      this.menuName = "BUMP_OR_DELETE"
    } else {
      this.args.goBack()
    }
  }

  @action
  setAnnotation(annotationArgs: {
    title: string
    description: string
    startDate: string
    endDate: string
  }) {
    this.annotationArgs = annotationArgs

    // In case the user expanded the range, check again for dangling lessons.
    if (this.bumpDeleteOrNothing === "NOTHING") {
      let lessonCount = 0
      for (const course of this.args.courses) {
        lessonCount += this.courseCalendarEditor.countCourseDateLessons(
          course,
          annotationArgs.startDate,
          annotationArgs.endDate
        )
      }
      if (lessonCount > 0) {
        this.menuName = "BUMP_OR_DELETE_2"
        this.moreThanOneLesson = lessonCount > 1
        return
      }
    }

    this.finish()
  }

  @action
  goBackFromBumpOrDelete2() {
    this.menuName = "EDIT_ANNOTATION"
  }

  @action
  afterBumpOrDelete2(bumpDeleteOrNothing: "BUMP" | "DELETE") {
    this.bumpDeleteOrNothing = bumpDeleteOrNothing
    this.finish()
  }

  private finish() {
    const { title, description, startDate, endDate } = this.annotationArgs!
    this.planbookEditor.addAnnotation({
      title: title,
      description: description,
      startDate: startDate,
      endDate: endDate,
      isOff: true,
      models: {
        planbook: this.args.planbook,
        rotationCalendar: this.args.rotationCalendar,
        courses: this.args.courses,
      },
      bumpDeleteOrNothing: this.bumpDeleteOrNothing,
    })
    this.args.closeMenu()
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Planner::Menus::DayMenu::AddNoSchoolDay": typeof DayMenuAddNoSchoolDayComponent
  }
}
